"use client";

import { createContext, useContext, type ReactNode } from "react";
import type { Dict } from "./types";
import { type Locale, isRtl as computeRtl } from "./config";

type LocaleContextValue = { locale: Locale; t: Dict; isRtl: boolean };

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

/**
 * Thin client locale provider.
 *
 * The server resolves the active locale from the URL (the [locale] segment;
 * the default locale is served unprefixed) and seeds this with ONLY that
 * locale's dictionary. Client islands read it via useLocale(). There's no
 * state, no localStorage, no all-locale bundle, and no English-then-swap: the
 * SSR HTML is already in the right language because the server picked it, so
 * search engines and the first paint both see the correct locale.
 *
 * Switching locale is navigation (see LocaleSwitcher), not a state update.
 */
export function LocaleProvider({
  locale,
  dict,
  children,
}: {
  locale: Locale;
  dict: Dict;
  children: ReactNode;
}) {
  return (
    <LocaleContext.Provider
      value={{ locale, t: dict, isRtl: computeRtl(locale) }}
    >
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used inside <LocaleProvider>.");
  }
  return ctx;
}

export type { Dict };
export type { Locale } from "./config";
